import { FormControlLabel, Grid, Radio, RadioGroup } from "@mui/material";
import { ResizeComponent, type SizeProps } from "containers/hooks/ResizeComponent";
import * as d3 from "d3";
import { type SankeyLinkMinimal, type SankeyNodeMinimal, sankey, sankeyLinkHorizontal } from "d3-sankey";
import { useCallback, useMemo, useState } from "react";
import { useDatapointsFromExportId } from "@/containers/hooks/useData";
import { Category, type DataPoint } from "@/fileParsing/types";
import { removeDuplicates } from "@/utils/array";
import { deserializeOutputs } from "./parserProcessing";
import type { ParserJSON } from "./policyParserTypes";
import TooltipWrapper from "./tooltipWrapper";

const sharingParties = [
  "Advertisers",
  "Partners and Affiliates",
  "Third party service providers",
  "Authorities",
  "General public",
  "Others",
];
const purposes = [
  "Create and manage account",
  "Provide services, products, features",
  "Connect with 3rd party service",
  "Provide customer support",
  "Improve and personalize services",
  "Process transactions or payments",
  "Train Machine Learning or AI models",
  "Measurement and analytics",
  "Security and identification",
  "Targeted and personalized advertisements",
  "Evaluate safety of content and content moderation",
  "Research and development",
  "Comply with legal requirements and authorities",
  "Other",
];

// Settings for tooltip
type TooltipSettings = {
  tooltipWidth: number;
  margin: number;
};
const tooltipSettings: TooltipSettings = {
  tooltipWidth: 300,
  margin: 10,
};

type Node = { id: number; type: string; name: string };
export type Sensitivity = "not sensitive" | "partially sensitive" | "sensitive";
export type Link = {
  source: number;
  target: number;
  value: number;
  count: number;
  catAsSource: boolean;
  sensitivity?: Sensitivity;
};
type SankeyNode = SankeyNodeMinimal<Node, Link> & Node;
type SankeyLink = SankeyLinkMinimal<Node, Link> & Link;

type VisualizationSettings = {
  buttonHeight: number;
  nodeWidth: number;
  nodePadding: number;
  categoryColor: string;
  verticalMargin: number;
};

type ComponentProps = {
  exportIds?: number[];
  providerIds: string[];
  settings?: Partial<VisualizationSettings>;
};

const sensitivityRank = new Map<Sensitivity, number>();
sensitivityRank.set("not sensitive", 0);
sensitivityRank.set("partially sensitive", 1);
sensitivityRank.set("sensitive", 2);

export function CatToNumOfDataPoints(
  data: DataPoint[],
  links: Link[],
  nodes: Node[],
  catType: string,
): Map<string, number> {
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  // (catName, clusterName) -> count
  const counts = new Map<string, number>();
  for (const dp of data) {
    const cats = catType === "TVCategory" ? [dp.category] : (dp.policyCategory ?? []);
    const purpose = Array.from(dp.purposes).sort().join(", ");
    const sharing = Array.from(dp.sharing).sort().join(", ");
    for (const cat of cats) {
      for (const cluster of [purpose, sharing]) {
        const key = `${cat}\u0000${cluster}`;
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    }
  }

  const result = new Map<string, number>();
  for (const link of links) {
    const s = nodeById.get(link.source);
    const t = nodeById.get(link.target);
    const [catNode, attrNode] = s.type.includes("category") ? [s, t] : [t, s];
    result.set(`${link.source}-${link.target}`, counts.get(`${catNode.name}\u0000${attrNode.name}`) ?? 0);
  }
  return result;
}

function getClusterNames(full_data: DataPoint[], node_type: string) {
  const clusterNames: string[] = [];

  full_data.forEach((dp) => {
    if (node_type === "sharing party") {
      clusterNames.push([...dp.sharing].sort().join(", "));
    } else {
      clusterNames.push([...dp.purposes].sort().join(", "));
    }
  });

  return removeDuplicates(clusterNames);
}

function filterCatNodes(nodes: Node[], links: Link[]): Node[] {
  const filtered_nodes = nodes.filter((node) =>
    links.some((link) => link.source === node.id || link.target === node.id),
  );
  return filtered_nodes;
}

function getSankeyData(
  extractedDataTypes: ParserJSON[],
  full_data: DataPoint[],
  catType: string,
  visualization_type: string,
  catNodes: Node[],
) {
  const all_nodes: Node[] = [];
  const all_links: Link[] = [];
  const IdOffset = catNodes.length;
  const clusterNamesSharing = visualization_type !== "purpose" ? getClusterNames(full_data, "sharing party") : [];
  const clusterNamesPurpose = visualization_type !== "sharing party" ? getClusterNames(full_data, "purpose") : [];

  if (visualization_type === "purpose") {
    const data = getSankeyDataPurposes(full_data, catNodes, clusterNamesPurpose, IdOffset, true, catType);
    all_nodes.push(...data.nodes);
    all_links.push(...data.links);
  } else if (visualization_type === "sharing party") {
    const data = getSankeyDataSharing(full_data, catNodes, clusterNamesSharing, IdOffset, true, catType);
    all_nodes.push(...data.nodes);
    all_links.push(...data.links);
  } else if (visualization_type === "purposes&sharing") {
    const dataSharing = getSankeyDataSharing(full_data, catNodes, clusterNamesSharing, IdOffset, true, catType);
    all_nodes.push(...dataSharing.nodes);
    all_links.push(...dataSharing.links);
    const dataPurpose = getSankeyDataPurposes(
      full_data,
      catNodes,
      clusterNamesPurpose,
      clusterNamesSharing.length + IdOffset,
      false,
      catType,
    );
    all_nodes.push(...dataPurpose.nodes);
    all_links.push(...dataPurpose.links);
  }

  all_nodes.push(...filterCatNodes(catNodes, all_links));

  const CategoryToNumOfDataPoints = CatToNumOfDataPoints(full_data, all_links, all_nodes, catType);

  const linksWithValues = all_links.map((link) => {
    const sourceNode = all_nodes.find((node) => node.id === link.source);
    const targetNode = all_nodes.find((node) => node.id === link.target);

    const dataPointCount =
      sourceNode && targetNode ? (CategoryToNumOfDataPoints.get(`${link.source}-${link.target}`) ?? 0) : 0;

    const linkWithValue = {
      source: link.source,
      target: link.target,
      value: dataPointCount < 10 ? 10 : dataPointCount,
      count: dataPointCount,
      catAsSource: link.catAsSource,
    };

    if (catType !== "TVCategory") {
      const linkSensitivity = extractedDataTypes
        .flatMap((extractedDataType) => extractedDataType.outputs)
        .filter((output) => output.category === (sourceNode.type === "category" ? sourceNode.name : targetNode.name))
        .map((output) => output.sensitive as Sensitivity)
        .reduce((lowest, current) => (sensitivityRank.get(current) < sensitivityRank.get(lowest) ? current : lowest));
      return { ...linkWithValue, sensitivity: linkSensitivity };
    }

    return linkWithValue;
  });

  const filtered_links = linksWithValues.filter((link) => link.count > 0);
  const linkedNodeIds = new Set(filtered_links.flatMap((link) => [link.source, link.target]));
  const visibleNodes = all_nodes.filter((node) => linkedNodeIds.has(node.id));

  return { nodes: visibleNodes, links: filtered_links };
}

function getSankeyDataPurposes(
  dataPoints: DataPoint[],
  catNodes: Node[],
  clusterNames: string[],
  prevIdOffset: number,
  direction: boolean,
  catType: string,
) {
  const nodes: Node[] = [];

  const pur_nodes = clusterNames.map((name, index) => ({ id: prevIdOffset + index, type: "purpose", name }));

  const links = catNodes.flatMap((categoryNode) => {
    const categoryData = dataPoints.filter((dataPoint) =>
      catType === "TVCategory"
        ? dataPoint.category === categoryNode.name
        : dataPoint.policyCategory?.includes(categoryNode.name),
    );

    const targetClusters = removeDuplicates(categoryData.map((dataPoint) => [...dataPoint.purposes].sort().join(", ")));

    return pur_nodes
      .filter((purposeNode) => targetClusters.includes(purposeNode.name))
      .map((purposeNode) => ({
        source: direction ? categoryNode.id : purposeNode.id,
        target: direction ? purposeNode.id : categoryNode.id,
        value: 1,
        count: 1,
        catAsSource: direction,
      }));
  });

  nodes.push(
    ...pur_nodes.filter((pur_node) => links.some((link) => link.target === pur_node.id || link.source === pur_node.id)),
  );

  return { nodes, links };
}

function getSankeyDataSharing(
  dataPoints: DataPoint[],
  catNodes: Node[],
  clusterNames: string[],
  prevIdOffset: number,
  direction: boolean,
  catType: string,
) {
  const nodes: Node[] = [];

  const shar_nodes = clusterNames.map((name, index) => ({ id: prevIdOffset + index, type: "sharing party", name }));

  const links = catNodes.flatMap((categoryNode) => {
    const categoryData = dataPoints.filter((dataPoint) =>
      catType === "TVCategory"
        ? dataPoint.category === categoryNode.name
        : dataPoint.policyCategory?.includes(categoryNode.name),
    );

    const targetClusters = removeDuplicates(categoryData.map((dataPoint) => [...dataPoint.sharing].sort().join(", ")));

    return shar_nodes
      .filter((sharNode) => targetClusters.includes(sharNode.name))
      .map((sharNode) => ({
        source: direction ? categoryNode.id : sharNode.id,
        target: direction ? sharNode.id : categoryNode.id,
        value: 1,
        count: 1,
        catAsSource: direction,
      }));
  });

  nodes.push(
    ...shar_nodes.filter((shar_node) =>
      links.some((link) => link.target === shar_node.id || link.source === shar_node.id),
    ),
  );

  return { nodes, links };
}

function getPolicyCategoryNodes(extractedDataTypes: ParserJSON[]): Node[] {
  const catNodesNames: string[] = [];
  const idOffset = 0;

  extractedDataTypes.forEach((extractedDataType) => {
    const categories = extractedDataType.outputs.map((output) => output.category);
    catNodesNames.push(...categories);
  });

  const catNodes = removeDuplicates(catNodesNames).map((catName, index) => ({
    id: idOffset + index,
    type: "category",
    name: catName,
  }));

  return catNodes;
}

function getTVCategoryNodes(): Node[] {
  const catNodes: Node[] = [];
  let idOffset = 0;

  for (const key in Category) {
    const tempCatNode = { id: idOffset, type: "category", name: key };
    catNodes.push(tempCatNode);
    idOffset += 1;
  }

  return catNodes;
}

export const VisualizationSettingsDefaults: VisualizationSettings = {
  buttonHeight: 32,
  nodeWidth: 12,
  nodePadding: 12,
  categoryColor: "#ecd71b",
  verticalMargin: 24,
};

const ColorSharing = d3.scaleOrdinal<string>().domain(sharingParties).range(d3.schemeSet2);

const ColorPurposes = d3.scaleOrdinal<string>().domain(purposes).range(d3.schemeSet2);

const nodeFill = (node: Node, categoryColor: string) =>
  node.type === "category"
    ? categoryColor
    : node.type === "sharing party"
      ? ColorSharing(node.name)
      : ColorPurposes(node.name);

/** Two leading comma-separated parts, then an ellipsis. */
function truncateLabel(name: string): string {
  const parts = name.split(/(?<=,)/);
  return parts.length >= 2 ? `${parts.slice(0, 2).join("")} ... ` : name;
}

const linkPath = sankeyLinkHorizontal<Node, Link>();

type TooltipState = {
  visible: boolean;
  position: { x: number; y: number };
  link?: Link;
  linkSourceName: string;
  linkTargetName: string;
};

const tooltipHidden: TooltipState = {
  visible: false,
  position: { x: 0, y: 0 },
  link: undefined,
  linkSourceName: "",
  linkTargetName: "",
};

function SankeyChart(props: ComponentProps & SizeProps) {
  const full_data = useDatapointsFromExportId(props.exportIds);
  const [catType, setCatType] = useState("policyCategory");
  const [targetType, setTargetType] = useState("sharing");
  const [tooltip, setTooltip] = useState<TooltipState>(tooltipHidden);

  const settings = useMemo(() => ({ ...VisualizationSettingsDefaults, ...props.settings }), [props.settings]);

  const width = props.size.width;
  const height = props.size.height - settings.buttonHeight;

  const relevantJSON = useMemo(
    () => Object.values(deserializeOutputs(props.providerIds)).map(([, json]) => json as ParserJSON),
    [props.providerIds],
  );

  const catNodes = useMemo(
    () => (catType === "TVCategory" ? getTVCategoryNodes() : getPolicyCategoryNodes(relevantJSON)),
    [catType, relevantJSON],
  );

  const visualizationType =
    targetType === "sharing" ? "sharing party" : targetType === "purposes" ? "purpose" : targetType;

  const { nodes, links } = useMemo(
    () => getSankeyData(relevantJSON, full_data, catType, visualizationType, catNodes),
    [relevantJSON, full_data, catType, visualizationType, catNodes],
  );

  // Layout
  const layout = useMemo(() => {
    if (width <= 0 || height <= 0 || nodes.length === 0 || links.length === 0) return undefined;

    return sankey<Node, Link>()
      .nodeId((d) => d.id)
      .nodeWidth(settings.nodeWidth)
      .nodePadding(settings.nodePadding)
      .extent([
        [1, 1],
        [width - 1, height - settings.verticalMargin],
      ])({
      nodes: nodes.map((d) => ({ ...d })),
      links: links.map((d) => ({ ...d })),
    }) as { nodes: SankeyNode[]; links: SankeyLink[] };
  }, [nodes, links, width, height, settings.nodeWidth, settings.nodePadding, settings.verticalMargin]);

  const showTooltip = useCallback((event: React.MouseEvent<SVGPathElement>, link: SankeyLink) => {
    const source = link.source as unknown as Node;
    const target = link.target as unknown as Node;
    setTooltip({
      visible: true,
      position: { x: event.clientX, y: event.clientY },
      link,
      linkSourceName: source.name,
      linkTargetName: target.name,
    });
  }, []);

  const moveTooltip = useCallback((event: React.MouseEvent<SVGPathElement>) => {
    setTooltip((prev) => (prev.visible ? { ...prev, position: { x: event.clientX, y: event.clientY } } : prev));
  }, []);

  const hideTooltip = useCallback(() => {
    setTooltip((prev) => (prev.visible ? { ...prev, visible: false } : prev));
  }, []);

  const linkElements = useMemo(
    () =>
      layout?.links.map((link) => {
        const target = link.target as unknown as SankeyNode;
        return (
          // biome-ignore lint/a11y/noStaticElementInteractions: I dont know how else to do this
          <path
            key={`${(link.source as unknown as Node).id}-${target.id}`}
            d={linkPath(link) ?? undefined}
            stroke={target.type === "sharing party" ? ColorSharing(target.name) : ColorPurposes(target.name)}
            strokeWidth={link.width ?? 0}
            onMouseEnter={(event) => showTooltip(event, link)}
            onMouseMove={moveTooltip}
            onMouseLeave={hideTooltip}
          />
        );
      }),
    [layout, showTooltip, moveTooltip, hideTooltip],
  );

  const nodeElements = useMemo(
    () =>
      layout?.nodes.map((node) => {
        const labelOnRight = (node.x0 ?? 0) < width / 2;
        return (
          <g key={node.id}>
            <rect
              x={node.x0 ?? 0}
              y={node.y0 ?? 0}
              width={(node.x1 ?? 0) - (node.x0 ?? 0)}
              height={(node.y1 ?? 0) - (node.y0 ?? 0)}
              fill={nodeFill(node, settings.categoryColor)}
            />
            <text
              x={labelOnRight ? (node.x1 ?? 0) + 6 : (node.x0 ?? 0) - 6}
              y={((node.y1 ?? 0) + (node.y0 ?? 0)) / 2}
              dy="0.35em"
              fontSize={11}
              fill="#f5f5f5"
              textAnchor={labelOnRight ? "start" : "end"}
              pointerEvents="none"
            >
              {truncateLabel(node.name)}
            </text>
          </g>
        );
      }),
    [layout, width, settings.categoryColor],
  );

  if (links.length === 0) return null;

  return (
    <Grid container direction="row" sx={{ height: "100%", overflow: "hidden" }}>
      <Grid item xs={6}>
        <RadioGroup row name="category buttons" value={catType} onChange={(event) => setCatType(event.target.value)}>
          <FormControlLabel value="TVCategory" control={<Radio />} label="Category" />
          <FormControlLabel value="policyCategory" control={<Radio />} label="Policy Category" />
        </RadioGroup>
      </Grid>
      <Grid item xs={6} alignItems="flex-end">
        <RadioGroup
          row
          name="target buttons"
          value={targetType}
          onChange={(event) => setTargetType(event.target.value)}
        >
          <FormControlLabel value="purposes" control={<Radio />} label="Purposes" />
          <FormControlLabel value="sharing" control={<Radio />} label="Sharing" />
          <FormControlLabel value="purposes&sharing" control={<Radio />} label="Purposes & Sharing" />
        </RadioGroup>
      </Grid>
      <Grid item xs={12} sx={{ height: "100%", minHeight: 0, overflow: "hidden" }}>
        <svg
          width="100%"
          height="100%"
          xmlns="http://www.w3.org/2000/svg"
          viewBox={`0 0 ${width} ${height}`}
          style={{ display: "block", overflow: "hidden" }}
          onMouseLeave={hideTooltip}
          role="img"
          aria-label={`Sankey diagram, ${nodes.length} nodes and ${links.length} links`}
        >
          {/* Hover highlight in CSS so mouse movement don't cause re-renders. */}
          <style>{`.sankey-links path{fill:none;stroke-opacity:.4}.sankey-links path:hover{stroke-opacity:.7}`}</style>
          <g className="sankey-links">{linkElements}</g>
          <g className="sankey-nodes">{nodeElements}</g>
        </svg>
      </Grid>
      <TooltipWrapper
        visible={tooltip.visible}
        position={tooltip.position}
        settings={tooltipSettings}
        link={tooltip.link}
        linkSourceName={tooltip.linkSourceName}
        linkTargetName={tooltip.linkTargetName}
      />
    </Grid>
  );
}

export default ResizeComponent(SankeyChart);
